import { sql } from "drizzle-orm";
import { index, integer, real, sqliteTable, text, uniqueIndex } from "drizzle-orm/sqlite-core";

function auditColumns() {
  return {
    createdAt: text("created_at").notNull(),
    createdBy: text("created_by"),
    updatedAt: text("updated_at").notNull(),
    updatedBy: text("updated_by"),
    deletedAt: text("deleted_at"),
    deletedBy: text("deleted_by"),
  };
}

export const dormitorySettings = sqliteTable("dormitory_settings", {
  id: text("id").primaryKey(),
  companyId: text("company_id").notNull(),
  defaultRentAmount: real("default_rent_amount").default(0).notNull(),
  defaultWaterRate: real("default_water_rate").default(0).notNull(),
  defaultElectricRate: real("default_electric_rate").default(0).notNull(),
  defaultServiceCharge: real("default_service_charge").default(0).notNull(),
  defaultDueDay: integer("default_due_day").default(5).notNull(),
  gracePeriodDays: integer("grace_period_days").default(0).notNull(),
  rentChargeTiming: text("rent_charge_timing", { enum: ["start_of_period", "end_of_period", "manual"] }).default("start_of_period").notNull(),
  billingCycleStartDay: integer("billing_cycle_start_day").default(1).notNull(),
  invoiceIssueDay: integer("invoice_issue_day").default(1).notNull(),
  proratePolicy: text("prorate_policy", { enum: ["none", "daily"] }).default("daily").notNull(),
  utilityBillingMode: text("utility_billing_mode", { enum: ["metered", "flat", "included"] }).default("metered").notNull(),
  advanceRentMonths: integer("advance_rent_months").default(1).notNull(),
  depositMonths: integer("deposit_months").default(1).notNull(),
  lateFeeType: text("late_fee_type", { enum: ["fixed", "percent"] }).default("fixed").notNull(),
  lateFeeAmount: real("late_fee_amount").default(0).notNull(),
  invoiceItemId: text("invoice_item_id"),
  incomeAccountId: text("income_account_id"),
  waterItemId: text("water_item_id"),
  electricItemId: text("electric_item_id"),
  notes: text("notes"),
  ...auditColumns(),
}, (table) => ({
  companyIdx: uniqueIndex("ds_company_idx").on(table.companyId),
}));

export const dormitoryBuildings = sqliteTable("dormitory_buildings", {
  id: text("id").primaryKey(),
  companyId: text("company_id").notNull(),
  branchId: text("branch_id"),
  code: text("code").notNull(),
  name: text("name").notNull(),
  address: text("address"),
  totalFloors: integer("total_floors").default(1).notNull(),
  contactName: text("contact_name"),
  contactPhone: text("contact_phone"),
  isActive: integer("is_active", { mode: "boolean" }).default(true).notNull(),
  notes: text("notes"),
  ...auditColumns(),
}, (table) => ({
  companyCodeIdx: uniqueIndex("db_company_code_idx").on(table.companyId, table.code).where(sql`${table.deletedAt} IS NULL`),
  companyActiveIdx: index("db_company_active_idx").on(table.companyId, table.isActive),
}));

export const dormitoryRooms = sqliteTable("dormitory_rooms", {
  id: text("id").primaryKey(),
  companyId: text("company_id").notNull(),
  buildingId: text("building_id").notNull().references(() => dormitoryBuildings.id),
  roomNo: text("room_no").notNull(),
  floorNo: integer("floor_no").default(1).notNull(),
  roomType: text("room_type"),
  status: text("status", { enum: ["available", "occupied", "maintenance", "inactive"] }).default("available").notNull(),
  rentAmount: real("rent_amount").default(0).notNull(),
  depositAmount: real("deposit_amount").default(0).notNull(),
  waterMeterNo: text("water_meter_no"),
  electricMeterNo: text("electric_meter_no"),
  areaSqm: real("area_sqm"),
  maxOccupants: integer("max_occupants").default(1).notNull(),
  notes: text("notes"),
  ...auditColumns(),
}, (table) => ({
  companyBuildingIdx: index("dr_company_building_idx").on(table.companyId, table.buildingId),
  companyStatusIdx: index("dr_company_status_idx").on(table.companyId, table.status),
  roomUniqueIdx: uniqueIndex("dr_building_room_idx").on(table.buildingId, table.roomNo).where(sql`${table.deletedAt} IS NULL`),
}));

export const dormitoryTenants = sqliteTable("dormitory_tenants", {
  id: text("id").primaryKey(),
  companyId: text("company_id").notNull(),
  customerId: text("customer_id"),
  tenantCode: text("tenant_code"),
  fullName: text("full_name").notNull(),
  phone: text("phone"),
  email: text("email"),
  nationalId: text("national_id"),
  address: text("address"),
  emergencyContactName: text("emergency_contact_name"),
  emergencyContactPhone: text("emergency_contact_phone"),
  isActive: integer("is_active", { mode: "boolean" }).default(true).notNull(),
  notes: text("notes"),
  ...auditColumns(),
}, (table) => ({
  companyNameIdx: index("dt_company_name_idx").on(table.companyId, table.fullName),
  companyCodeIdx: index("dt_company_code_idx").on(table.companyId, table.tenantCode),
}));

export const dormitoryLeases = sqliteTable("dormitory_leases", {
  id: text("id").primaryKey(),
  companyId: text("company_id").notNull(),
  leaseNo: text("lease_no").notNull(),
  roomId: text("room_id").notNull().references(() => dormitoryRooms.id),
  tenantId: text("tenant_id").notNull().references(() => dormitoryTenants.id),
  startDate: text("start_date").notNull(),
  endDate: text("end_date"),
  moveInDate: text("move_in_date"),
  moveOutDate: text("move_out_date"),
  rentAmount: real("rent_amount").default(0).notNull(),
  depositAmount: real("deposit_amount").default(0).notNull(),
  billingDay: integer("billing_day").default(1).notNull(),
  status: text("status", { enum: ["draft", "active", "ended", "cancelled"] }).default("draft").notNull(),
  notes: text("notes"),
  ...auditColumns(),
}, (table) => ({
  companyStatusIdx: index("dl_company_status_idx").on(table.companyId, table.status),
  companyRoomIdx: index("dl_company_room_idx").on(table.companyId, table.roomId),
  companyTenantIdx: index("dl_company_tenant_idx").on(table.companyId, table.tenantId),
  leaseNoIdx: uniqueIndex("dl_company_lease_no_idx").on(table.companyId, table.leaseNo).where(sql`${table.deletedAt} IS NULL`),
}));

export const dormitoryBillingPeriods = sqliteTable("dormitory_billing_periods", {
  id: text("id").primaryKey(),
  companyId: text("company_id").notNull(),
  periodNo: text("period_no").notNull(),
  name: text("name").notNull(),
  periodMonth: text("period_month").notNull(),
  startDate: text("start_date").notNull(),
  endDate: text("end_date").notNull(),
  dueDate: text("due_date").notNull(),
  status: text("status", { enum: ["draft", "calculated", "closed"] }).default("draft").notNull(),
  notes: text("notes"),
  ...auditColumns(),
}, (table) => ({
  companyPeriodIdx: uniqueIndex("dbp_company_period_idx").on(table.companyId, table.periodMonth).where(sql`${table.deletedAt} IS NULL`),
  companyStatusIdx: index("dbp_company_status_idx").on(table.companyId, table.status),
}));

export const dormitoryMeterReadings = sqliteTable("dormitory_meter_readings", {
  id: text("id").primaryKey(),
  companyId: text("company_id").notNull(),
  billingPeriodId: text("billing_period_id").references(() => dormitoryBillingPeriods.id),
  roomId: text("room_id").notNull().references(() => dormitoryRooms.id),
  leaseId: text("lease_id").references(() => dormitoryLeases.id),
  meterType: text("meter_type", { enum: ["water", "electric"] }).notNull(),
  previousReading: real("previous_reading").default(0).notNull(),
  currentReading: real("current_reading").default(0).notNull(),
  usage: real("usage").default(0).notNull(),
  unitRate: real("unit_rate").default(0).notNull(),
  amount: real("amount").default(0).notNull(),
  readingDate: text("reading_date").notNull(),
  status: text("status", { enum: ["draft", "confirmed", "void"] }).default("draft").notNull(),
  adjustmentReason: text("adjustment_reason"),
  notes: text("notes"),
  ...auditColumns(),
}, (table) => ({
  companyPeriodIdx: index("dmr_company_period_idx").on(table.companyId, table.billingPeriodId),
  roomMeterIdx: index("dmr_room_meter_idx").on(table.roomId, table.meterType),
  periodRoomMeterIdx: uniqueIndex("dmr_period_room_meter_idx").on(table.billingPeriodId, table.roomId, table.meterType).where(sql`${table.deletedAt} IS NULL`),
}));

export const dormitoryBills = sqliteTable("dormitory_bills", {
  id: text("id").primaryKey(),
  companyId: text("company_id").notNull(),
  billNo: text("bill_no").notNull(),
  billingPeriodId: text("billing_period_id").notNull().references(() => dormitoryBillingPeriods.id),
  leaseId: text("lease_id").notNull().references(() => dormitoryLeases.id),
  roomId: text("room_id").notNull().references(() => dormitoryRooms.id),
  tenantId: text("tenant_id").notNull().references(() => dormitoryTenants.id),
  billDate: text("bill_date").notNull(),
  dueDate: text("due_date").notNull(),
  rentAmount: real("rent_amount").default(0).notNull(),
  waterAmount: real("water_amount").default(0).notNull(),
  electricAmount: real("electric_amount").default(0).notNull(),
  serviceAmount: real("service_amount").default(0).notNull(),
  discountAmount: real("discount_amount").default(0).notNull(),
  lateFeeAmount: real("late_fee_amount").default(0).notNull(),
  totalAmount: real("total_amount").default(0).notNull(),
  paidAmount: real("paid_amount").default(0).notNull(),
  status: text("status", { enum: ["draft", "issued", "paid", "cancelled"] }).default("draft").notNull(),
  issuedAt: text("issued_at"),
  paidAt: text("paid_at"),
  accountingInvoiceId: text("accounting_invoice_id"),
  accountingReceiptId: text("accounting_receipt_id"),
  notes: text("notes"),
  ...auditColumns(),
}, (table) => ({
  companyBillNoIdx: uniqueIndex("dbill_company_bill_no_idx").on(table.companyId, table.billNo).where(sql`${table.deletedAt} IS NULL`),
  periodLeaseIdx: uniqueIndex("dbill_period_lease_idx").on(table.billingPeriodId, table.leaseId).where(sql`${table.deletedAt} IS NULL`),
  companyStatusIdx: index("dbill_company_status_idx").on(table.companyId, table.status),
  companyDueIdx: index("dbill_company_due_idx").on(table.companyId, table.dueDate),
}));

export const dormitoryBillLines = sqliteTable("dormitory_bill_lines", {
  id: text("id").primaryKey(),
  companyId: text("company_id").notNull(),
  billId: text("bill_id").notNull().references(() => dormitoryBills.id),
  lineType: text("line_type", { enum: ["rent", "water", "electric", "service", "late_fee", "discount", "other"] }).notNull(),
  description: text("description").notNull(),
  quantity: real("quantity").default(1).notNull(),
  unitRate: real("unit_rate").default(0).notNull(),
  amount: real("amount").default(0).notNull(),
  sortOrder: integer("sort_order").default(0).notNull(),
  ...auditColumns(),
}, (table) => ({
  billIdx: index("dbl_bill_idx").on(table.billId),
  companyBillIdx: index("dbl_company_bill_idx").on(table.companyId, table.billId),
}));

export type DormitorySettings = typeof dormitorySettings.$inferSelect;
export type NewDormitorySettings = typeof dormitorySettings.$inferInsert;
export type DormitoryBuilding = typeof dormitoryBuildings.$inferSelect;
export type NewDormitoryBuilding = typeof dormitoryBuildings.$inferInsert;
export type DormitoryRoom = typeof dormitoryRooms.$inferSelect;
export type NewDormitoryRoom = typeof dormitoryRooms.$inferInsert;
export type DormitoryTenant = typeof dormitoryTenants.$inferSelect;
export type NewDormitoryTenant = typeof dormitoryTenants.$inferInsert;
export type DormitoryLease = typeof dormitoryLeases.$inferSelect;
export type NewDormitoryLease = typeof dormitoryLeases.$inferInsert;
export type DormitoryBillingPeriod = typeof dormitoryBillingPeriods.$inferSelect;
export type NewDormitoryBillingPeriod = typeof dormitoryBillingPeriods.$inferInsert;
export type DormitoryMeterReading = typeof dormitoryMeterReadings.$inferSelect;
export type NewDormitoryMeterReading = typeof dormitoryMeterReadings.$inferInsert;
export type DormitoryBill = typeof dormitoryBills.$inferSelect;
export type NewDormitoryBill = typeof dormitoryBills.$inferInsert;
export type DormitoryBillLine = typeof dormitoryBillLines.$inferSelect;
export type NewDormitoryBillLine = typeof dormitoryBillLines.$inferInsert;
